import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { useLang } from "@/i18n";

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div
      data-testid="lang-toggle"
      className="flex items-center border border-white/20 text-[11px] font-bold tracking-[0.15em]"
    >
      {["id", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-3 py-1.5 uppercase transition-colors duration-300 cursor-pointer ${
            lang === l ? "bg-[#D4AF37] text-black" : "text-neutral-400 hover:text-white"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const Header = () => {
  const [open, setOpen] = useState(false);
  const { t } = useLang();
  const LINKS = t.nav.links;

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  const bookCall = () => {
    sessionStorage.setItem("fb-topic-idx", "0");
    window.dispatchEvent(new Event("fb-topic"));
    go("contact");
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-[100] bg-black/60 backdrop-blur-xl border-b border-white/10"
    >
      <div className="container-x flex items-center justify-between h-[72px]">
        <button
          data-testid="header-logo"
          onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
          className="font-display text-2xl md:text-3xl tracking-wider text-white cursor-pointer"
        >
          FIT<span className="text-[#D91624]">BERSAMA</span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400 hover:text-white transition-colors duration-300 cursor-pointer"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <LangToggle />
          <button
            data-testid="header-book-call-btn"
            onClick={bookCall}
            className="hidden md:inline-flex btn-primary !px-6 !py-3"
          >
            {t.nav.bookCall}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white p-2 cursor-pointer"
            aria-label={t.nav.menuLabel}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-black/90 backdrop-blur-xl border-b border-white/10"
          >
            <div className="container-x py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left text-sm font-bold uppercase tracking-[0.2em] text-neutral-300 hover:text-[#D4AF37] transition-colors duration-300 cursor-pointer"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-book-call-btn"
                onClick={bookCall}
                className="btn-primary w-full"
              >
                {t.nav.bookCall}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
